import type { ComponentProps, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft as ArrowLeftIcon, PanelRightClose as PanelRightCloseIcon } from "lucide-react";
import { SimpleTooltip } from "../../../components/ui/tooltip/index.js";
import { cn } from "../../../lib/utils.js";
import { InboxIconButton } from "../InboxIconButton.js";
import { ConversationDetailsContent } from "./ConversationDetailsContent.js";

/**
 * Details inspector. Desktop shows the single collapse control in this header;
 * mobile shows Back to the conversation instead.
 */
type DetailsContentProps = Omit<ComponentProps<typeof ConversationDetailsContent>, "className">;

export function ConversationDetailsPanel({
  closeControl,
  onClose,
  closeButtonRef,
  className,
  ...contentProps
}: DetailsContentProps & {
  closeControl: "collapse" | "back";
  onClose: () => void;
  closeButtonRef?: RefObject<HTMLButtonElement | null>;
  className?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const closeLabel = closeControl === "back" ? t("inbox.details.backToThread") : t("inbox.details.collapse");

  const closeButton = (
    <SimpleTooltip content={closeLabel}>
      <InboxIconButton
        ref={closeButtonRef}
        aria-label={closeLabel}
        aria-controls={closeControl === "collapse" ? "inbox-details" : undefined}
        onClick={onClose}
      >
        {closeControl === "back" ? (
          <ArrowLeftIcon size={18} className="rtl:rotate-180" aria-hidden="true" />
        ) : (
          <PanelRightCloseIcon size={18} className="rtl:-scale-x-100" aria-hidden="true" />
        )}
      </InboxIconButton>
    </SimpleTooltip>
  );

  return (
    <aside
      className={cn("flex h-full min-h-0 flex-col overflow-hidden bg-surface", className)}
      aria-label={t("inbox.details.panelLabel")}
    >
      <header className="flex min-h-[60px] shrink-0 items-center gap-1.5 border-b border-inbox-line-soft ps-4 pe-2.5">
        {closeControl === "back" ? <span className="-ms-2">{closeButton}</span> : null}
        <h2 className="m-0 min-w-0 flex-1 truncate text-[0.90625rem] font-semibold tracking-[-0.012em] text-inbox-strong rtl:tracking-normal">
          {t("inbox.details.title")}
        </h2>
        {closeControl === "collapse" ? closeButton : null}
      </header>
      <ConversationDetailsContent {...contentProps} />
    </aside>
  );
}
